import { ChevronDownIcon, ChevronUpIcon } from '@village/icons';
import { Link } from '@village/ui';
import { NextAvailableAppointments } from 'components/next-available-appointments';
import { PrnBadge } from 'components/prn-badge';
import { SchedulingNotAvailable } from 'components/scheduling-not-available';
import { SLOTS_PER_COLUMN } from 'constants/appointment';
import { addDays } from 'date-fns';
import { useAppointmentsRange, useBooking, useCaptureCountlyEvent, useIsMobile } from 'hooks';
import { groupBy, range } from 'ramda';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Appointment, AppointmentsByDepartmentProvider } from 'types';
import { formatDate } from 'utils/date';
import { getStandardDepartmentAddress } from 'utils/department';
import { getAltText } from 'utils/image';

import { AppointmentsColumn } from './appointments-column';
import { ProviderBio } from './provider-bio';
import * as Styled from './styles';

interface Props {
    appointmentsGroup: AppointmentsByDepartmentProvider;
    isAppointmentsFetching: boolean;
}

const AppointmentCard: FC<Props> = ({ appointmentsGroup, isAppointmentsFetching }) => {
    const isMobile = useIsMobile();
    const { booking } = useBooking();
    const { datesRange } = useAppointmentsRange();
    const { provider, department, appointments } = appointmentsGroup;
    const [slotsPerColumn, setSlotsPerColumn] = useState(SLOTS_PER_COLUMN);
    const [canExpand, setCanExpand] = useState<boolean>(false);
    const { addCountlyEvent } = useCaptureCountlyEvent();

    const slotsCount = !canExpand ? appointments.length : slotsPerColumn;

    const cardRef = useRef<HTMLInputElement>(null);

    // To avoid queries waterfall when user quickly scrolls thought the cards.
    // Delay of 1 second before triggering the next available appointments query for the inView card.
    const { ref, inView } = useInView({ threshold: 0.3, delay: 1000, triggerOnce: true });

    const dates = range(0, datesRange).map((counter) => formatDate(addDays(booking.date, counter), 'monthDayYear'));

    const appointmentsGroupedByDate: Record<string, Appointment[] | undefined> = groupBy(
        (appointment) => appointment.date,
        appointments
    );

    const mappableGroupings = Object.values(appointmentsGroupedByDate);
    const maxSlots = Math.max(...mappableGroupings.map((group) => (group !== undefined ? group.length : 0)), 0);

    // Checks if every visible date column has additional visible dates to determine View more/View less toggle
    useEffect(() => {
        setCanExpand(mappableGroupings.some((group) => group !== undefined && group?.length > slotsPerColumn));
    }, [isMobile, slotsPerColumn, mappableGroupings]);

    // Check if toggle should be visible depending on number of available dates in the grouping
    const displayToggle = mappableGroupings.some((group) => group !== undefined && group?.length > SLOTS_PER_COLUMN);

    const handleClickForMoreAvailability = () => {
        if (isMobile) {
            setSlotsPerColumn(Infinity);
        } else {
            setSlotsPerColumn((prev) => {
                if (prev + SLOTS_PER_COLUMN >= maxSlots) {
                    return maxSlots;
                } else {
                    return prev + SLOTS_PER_COLUMN;
                }
            });
        }
        addCountlyEvent({ key: 'showMoreAvailability' });
    };

    const handleClickForLessAvailability = () => {
        setSlotsPerColumn(SLOTS_PER_COLUMN);
        if (cardRef?.current) {
            cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        addCountlyEvent({ key: 'showLessAvailability' });
    };

    return (
        <Styled.RefWrapper ref={cardRef}>
            <Styled.Container ref={ref} data-provider-id={provider.providerid}>
                <Styled.Wrapper>
                    <Styled.ProviderContainer>
                        <Styled.ProviderDetails>
                            <Link href={provider.hubspot?.path}>
                                <Styled.ProviderImage src={provider.thumbnailUrl} alt={getAltText(provider)} />
                            </Link>
                            <div>
                                <Styled.ProviderName
                                    type="sub3"
                                    href={!provider.isprn ? provider.hubspot?.path : undefined}
                                    forwardedAs={!provider.isprn && provider.hubspot?.path ? 'a' : undefined}
                                >
                                    {provider.displayname}
                                </Styled.ProviderName>

                                {provider.isprn ? (
                                    <Styled.PrnNotice>
                                        <PrnBadge />
                                    </Styled.PrnNotice>
                                ) : null}

                                {provider.specialty ? (
                                    <Styled.ProviderSpecialty type="body1">{provider.specialty}</Styled.ProviderSpecialty>
                                ) : null}

                                <Styled.DepartmentContainer>
                                    <Styled.PinIcon color="#000" size="sm" />
                                    <Styled.Department type="body1">{getStandardDepartmentAddress(department)}</Styled.Department>
                                </Styled.DepartmentContainer>
                            </div>
                        </Styled.ProviderDetails>
                        {provider.hubspot?.bio && !isMobile ? <ProviderBio bio={provider.hubspot?.bio} /> : null}
                    </Styled.ProviderContainer>
                    <Fragment>
                        {provider.hubspot?.enabledForScheduling === false &&
                        !provider.isprn &&
                        provider.entitytype !== 'Non-person entity' ? (
                            <Styled.NoAppointmentsContainer>
                                <SchedulingNotAvailable compact location={booking.department?.hubspot} reasons={['DISABLED']} />
                            </Styled.NoAppointmentsContainer>
                        ) : !isAppointmentsFetching ? (
                            <Fragment>
                                {appointments.length > 0 ? (
                                    <Styled.AppointmentsContainer>
                                        <Styled.AppointmentsGroup>
                                            {dates.map((date) => (
                                                <AppointmentsColumn
                                                    key={date}
                                                    date={date}
                                                    slotsCount={slotsCount}
                                                    appointments={appointmentsGroupedByDate[date] ?? []}
                                                    maxSlots={maxSlots}
                                                />
                                            ))}
                                        </Styled.AppointmentsGroup>
                                        <Styled.AvailabilityControllerWrapper>
                                            {displayToggle ? (
                                                isMobile ? (
                                                    <Link
                                                        data-name={canExpand ? 'mobile-more' : 'mobile-less'}
                                                        data-testid={canExpand ? 'mobile-more' : 'mobile-less'}
                                                        rightIcon={
                                                            canExpand ? (
                                                                <Styled.ViewAllAvailabilityIcon />
                                                            ) : (
                                                                <Styled.ViewLessAvailabilityIcon />
                                                            )
                                                        }
                                                        onClick={() =>
                                                            canExpand
                                                                ? handleClickForMoreAvailability()
                                                                : handleClickForLessAvailability()
                                                        }
                                                    >
                                                        {canExpand ? 'View all availability' : 'View less availability'}
                                                    </Link>
                                                ) : (
                                                    <Link
                                                        data-name={canExpand ? 'desktop-more' : 'desktop-less'}
                                                        data-testid={canExpand ? 'desktop-more' : 'desktop-less'}
                                                        rightIcon={
                                                            canExpand ? (
                                                                <ChevronDownIcon size="sm" />
                                                            ) : (
                                                                <ChevronUpIcon size="sm" />
                                                            )
                                                        }
                                                        onClick={() =>
                                                            canExpand
                                                                ? handleClickForMoreAvailability()
                                                                : handleClickForLessAvailability()
                                                        }
                                                    >
                                                        {canExpand ? 'Show more appointments' : 'Show less availability'}
                                                    </Link>
                                                )
                                            ) : null}
                                        </Styled.AvailabilityControllerWrapper>
                                    </Styled.AppointmentsContainer>
                                ) : appointmentsGroup.provider.entitytype === 'Person' ? (
                                    <Styled.NoAppointmentsContainer data-testid="no-appointments">
                                        <NextAvailableAppointments grouping={appointmentsGroup} isInView={inView} />
                                    </Styled.NoAppointmentsContainer>
                                ) : null}
                            </Fragment>
                        ) : null}
                    </Fragment>
                </Styled.Wrapper>
            </Styled.Container>
        </Styled.RefWrapper>
    );
};

export { AppointmentCard };
